import { I18n } from "@assets/strings";
import { useObservable } from "micro-observables";
import React, { useMemo, useState } from "react";
import { useHistory } from "@core/routing/useHistory";
import styled, { css } from "styled-components";
import guideIllustration from "@assets/images/guide_illustration.svg";
import { Path } from "@core/routing/path";
import { Group } from "@domain/group/group";
import { ImageVersions } from "@domain/media/dtos";
import { ClosableModal } from "@ui/modals/closableModal";
import { BackgroundImage } from "@ui/components/backgroundImage";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { TertiaryButton } from "@ui/components/buttons/tertiaryButton";
import { Description } from "@ui/components/containers/description";
import { PrimaryBold } from "@ui/components/containers/primary";
import { Title } from "@ui/components/containers/title";
import { row, Stack, stack } from "@ui/components/layout";
import {
	ShareroomGuidecontroller,
	shareroomGuideSteps,
} from "@ui/components/group/shareroom/shareroomGuide/shareroomGuidecontroller/shareroomGuidecontroller";
import { ShareroomGuidecontrollerContext } from "@ui/components/group/shareroom/shareroomGuide/shareroomGuidecontroller/shareroomGuidecontrollerContext";
import { SHAREROOM_GUIDE_INFOS } from "./shareroomGuideInfos";
import { ShareroomGuideNavigation } from "./shareroomGuideNaviguation";
import { StepBubble } from "./stepBubble";

interface ShareroomGuideModalProps extends React.HTMLAttributes<HTMLDivElement> {
	close: () => void;
	shareroom: Group;
}

export const ShareroomGuideModal: React.FC<ShareroomGuideModalProps> = ({ close, shareroom, ...htmlProps }) => {
	const controller = useMemo(() => new ShareroomGuidecontroller(), []);
	const step = useObservable(controller.step);
	const isGuideEnd = step === "ACTIVITY";
	const image: string = SHAREROOM_GUIDE_INFOS[step];
	const [start, setStart] = useState<boolean>(false);
	const history = useHistory();

	return (
		<ShareroomGuidecontrollerContext.Provider value={controller}>
			<Container close={close} {...htmlProps} keyboardEventDisabled>
				{start ? (
					<>
						<StyledTitle pos={step !== "LIVE" && step !== "WORKSPACES" ? "LEFT" : "RIGHT"}>
							{I18n.t(`shareroom.guide.${step.toLocaleLowerCase()}.message`)}
						</StyledTitle>
						<StyledBackgroundImage defaultUrl={image} size={ImageVersions.MEDIUM} />
						<Content>
							<StepBubble steps={shareroomGuideSteps.length} currentStep={shareroomGuideSteps.indexOf(step)} />
							<Stack gap={25}>
								<Title>{I18n.t(`group.tab.${step.toLocaleLowerCase()}`)}</Title>
								<StyledDescription>
									{I18n.t(`shareroom.guide.${step.toLocaleLowerCase()}.description`)}
								</StyledDescription>
							</Stack>
							<Action>
								{isGuideEnd ? <PrimaryButton onClick={() => close()}>{I18n.t("finish")}</PrimaryButton> : null}

								<ShareroomGuideNavigation />
							</Action>
						</Content>
					</>
				) : (
					<Stack gap={60} align="center" justify="center">
						<Title>{I18n.t("shareroom.guide.title")}</Title>
						<img src={guideIllustration} />
						<StyledDescription>{I18n.t("shareroom.guide.description")}</StyledDescription>
						<Bloc>
							<StyledPrimaryButton onClick={() => setStart(true)}>{I18n.t("start")}</StyledPrimaryButton>
							<StyledTertiaryButton
								onClick={() => {
									history.push({ pathname: Path.Shareroom(shareroom), state: undefined });
									close();
								}}
							>
								{I18n.t("ignore")}
							</StyledTertiaryButton>
						</Bloc>
					</Stack>
				)}
			</Container>
		</ShareroomGuidecontrollerContext.Provider>
	);
};

const Container = styled(ClosableModal)`
	position: relative;
	padding: 50px 40px;
`;

const Content = styled.div`
	${stack(40, undefined, "flex-end")}
	margin-top: 365px;
`;

const StyledBackgroundImage = styled(BackgroundImage)`
	position: absolute;
	top: 0;
	left: 0;
	height: 385px;
	width: 100%;

	> :first-child {
		border-radius: 13px;
	}
`;

const Action = styled.div`
	width: 50%;
	gap: 80px;
	${row(0, "flex-end", "center")};
`;

const Bloc = styled.div`
	width: 100%;
	${row(0, "space-between", "center")};
`;

const StyledTertiaryButton = styled(TertiaryButton)`
	text-decoration: none;
	font-weight: 700;
`;

const StyledPrimaryButton = styled(PrimaryButton)`
	margin-left: calc(100% / 3);
`;
const StyledTitle = styled(PrimaryBold)<{ pos: "LEFT" | "RIGHT" }>`
	position: absolute;
	top: 45px;
	color: #fff;
	z-index: 9;
	width: 160px;
	font-size: 1rem;
	${({ pos }) =>
		pos === "LEFT"
			? css`
					left: 32px;
				`
			: css`
					right: 42px;
				`}
`;

const StyledDescription = styled(Description)`
	text-align: left;
`;
